/**
 * The `line` visual adapter (SPEC §9.4).
 *
 * An adapter's whole job is to translate a `VisualSpec` plus a §8.6 response
 * into an ECharts option object. It does no formatting of its own — axis
 * labels and tooltips call into `lib/format.ts`, so a currency on an axis
 * matches the same currency in a table cell.
 */

import type { EChartsOption } from 'echarts'

import {
  axisLabel,
  axisLabelFormatter,
  axisLine,
  axisTooltipFormatter,
  baseOption,
  baseTooltip,
  seriesColor,
  splitLine,
} from '../../lib/charts'
import type { Cell, ColumnSpec, VisualSpec } from '../../lib/types'
import { categoryData, resolveSeriesColumns, resolveXColumn, seriesData } from './cartesian'

export function lineOption(
  visual: VisualSpec,
  columns: readonly ColumnSpec[],
  rows: readonly (readonly Cell[])[],
  accent: string,
): EChartsOption {
  const xColumn = resolveXColumn(visual, columns)
  const categories = categoryData(visual, columns, rows)
  const seriesColumns = resolveSeriesColumns(visual, columns)

  const yColumn = seriesColumns[0]
  if (yColumn === undefined) {
    throw new Error(`visual ${visual.id} is a line with no series`)
  }

  return {
    ...baseOption(),
    tooltip: { ...baseTooltip(), formatter: axisTooltipFormatter(xColumn, seriesColumns) },
    xAxis: {
      type: 'category',
      data: categories,
      axisLabel: { ...axisLabel(), formatter: axisLabelFormatter(xColumn) },
      axisLine: axisLine(),
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLabel: { ...axisLabel(), formatter: axisLabelFormatter(yColumn) },
      splitLine: splitLine(),
    },
    series: visual.series.map((series, index) => ({
      type: 'line',
      name: seriesColumns[index]?.label ?? series.column,
      data: seriesData(columns, rows, series.column),
      itemStyle: { color: seriesColor(series, accent, index) },
      lineStyle: { width: 2 },
      symbol: 'circle',
      symbolSize: 5,
      // No smoothing. A smoothed line invents values between the points that
      // the query actually returned.
      smooth: false,
    })),
    // §9.2 chose ECharts partly for dataZoom; a long daily series needs it.
    // Omitted entirely on a short series rather than set to `undefined`, which
    // `exactOptionalPropertyTypes` treats as a different thing from absent.
    ...(rows.length > 60
      ? { dataZoom: [{ type: 'inside' as const }, { type: 'slider' as const, height: 18 }] }
      : {}),
  }
}
